import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, ShieldCheck, ArrowRight, Zap, Clock, CreditCard, Sparkles, Calendar, Briefcase, FileText, IndianRupee } from "lucide-react";
import { Link } from "react-router-dom";
import { FAQChatBot } from "@/components/FAQChatBot";
import { supabase } from "@/integrations/supabase/client";

interface PlatformStats {
  totalMerchants: number;
  totalRevenue: number;
  totalCustomers: number;
}

const formatCompactNumber = (num: number): string => {
  if (num >= 1000000) return `₹${(num / 1000000).toFixed(1)}M+`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(0)}K+`;
  return `₹${num.toLocaleString()}`;
};

function Index() {
  const [stats, setStats] = useState<PlatformStats | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-platform-stats");
        if (!error && data) setStats(data);
      } catch (err) {
        console.error("Failed to fetch platform stats:", err);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO url="/" />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center bg-gradient-to-br from-cyan-500/10 via-background to-teal-500/10 overflow-hidden">
          <div className="container py-20">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 text-sm font-medium mb-8">
                <Sparkles className="w-4 h-4" />
                Simplified client payment collections
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-foreground leading-tight mb-6">
                Collect <span className="bg-gradient-to-r from-cyan-600 to-teal-500 bg-clip-text text-transparent">Client Payments</span> Effortlessly
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Send payment links, track collections, auto-generate invoices — all in one place for freelancers & professionals.
              </p>
              
              <div className="flex flex-wrap justify-center gap-3 mb-10">
                <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">Freelancers</span>
                <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">Consultants</span>
                <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">Tutors</span>
                <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">Service Providers</span>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="text-lg px-8 h-14 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                  <Link to="/login?tab=signup">
                    Start Free — 3 Months Trial
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8 h-14 border-cyan-500 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-500 hover:text-white">
                  <Link to="/demo">
                    <Calendar className="mr-2 w-5 h-5" />
                    Book a Demo
                  </Link>
                </Button>
              </div>
              
              {stats && (
                <div className="mt-16 flex flex-wrap justify-center gap-8 md:gap-16 text-center">
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-cyan-600">{stats.totalMerchants}+</div>
                    <div className="text-sm text-muted-foreground mt-1">Professionals</div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-cyan-600">{formatCompactNumber(stats.totalRevenue)}</div>
                    <div className="text-sm text-muted-foreground mt-1">Collected</div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-foreground">90%</div>
                    <div className="text-sm text-muted-foreground mt-1">On-time Payments</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(185_80%_50%/0.1),transparent_50%)] pointer-events-none" />
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="container py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Three Simple Steps</h2>
            <p className="text-xl text-muted-foreground">Get paid faster with zero hassle</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="border-2 border-border hover:border-cyan-500/50 transition-colors text-center">
              <CardContent className="pt-8 pb-6 px-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Add Your Client</h3>
                <p className="text-muted-foreground text-sm">Add client details, set up services & payment schedule</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-border hover:border-cyan-500/50 transition-colors text-center">
              <CardContent className="pt-8 pb-6 px-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Send Payment Link</h3>
                <p className="text-muted-foreground text-sm">Client gets a secure one-click payment link via email</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-border hover:border-cyan-500/50 transition-colors text-center">
              <CardContent className="pt-8 pb-6 px-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Get Paid & Invoice</h3>
                <p className="text-muted-foreground text-sm">Automatic reminders, instant receipts & GST invoices</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Features Section */}
        <section className="bg-card border-y border-border">
          <div className="container py-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Built for Professionals</h2>
              <p className="text-xl text-muted-foreground">Everything you need to manage client payments</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Razorpay & UPI</h3>
                  <p className="text-muted-foreground text-sm">Accept payments via UPI, cards, net banking & more</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Auto Invoicing</h3>
                  <p className="text-muted-foreground text-sm">GST-compliant invoices generated automatically on payment</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Smart Reminders</h3>
                  <p className="text-muted-foreground text-sm">Automated payment reminders so you never chase clients</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Client Management</h3>
                  <p className="text-muted-foreground text-sm">Track all clients, subscriptions & payment history</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Secure Payments</h3>
                  <p className="text-muted-foreground text-sm">Bank-grade security for every transaction</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Quick Setup</h3>
                  <p className="text-muted-foreground text-sm">Sign up and start collecting in under 5 minutes</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Preview */}
        <section id="pricing" className="container py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-muted-foreground">Start free for 3 months. No credit card required.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-primary/30 hover:border-primary/50 transition-colors relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 to-teal-500" />
              <CardContent className="pt-8 pb-6 px-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-6 h-6 text-cyan-600" />
                  <h3 className="text-lg font-bold text-foreground">Free Trial</h3>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles className="w-4 h-4 text-cyan-500" />
                    <span className="font-medium text-cyan-600">3 months FREE</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4 text-teal-500" />
                    <span>Unlimited clients & payments</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FileText className="w-4 h-4 text-cyan-500" />
                    <span>Auto invoicing included</span>
                  </div>
                </div>
                <Button asChild className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                  <Link to="/login?tab=signup">Start Free</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-border hover:border-cyan-500/50 transition-colors relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 to-teal-500" />
              <CardContent className="pt-8 pb-6 px-6">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-6 h-6 text-cyan-600" />
                  <h3 className="text-lg font-bold text-foreground">Pay Per Use</h3>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <IndianRupee className="w-4 h-4 text-cyan-600" />
                    <span>₹10 per payment collected</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4 text-teal-500" />
                    <span>No monthly commitment</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Zap className="w-4 h-4 text-cyan-500" />
                    <span>All features included</span>
                  </div>
                </div>
                <Button asChild variant="outline" className="w-full border-cyan-500 text-cyan-700 hover:bg-cyan-500 hover:text-white">
                  <Link to="/pricing?tab=professional">View Details</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-border hover:border-primary/50 transition-colors relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-muted-foreground" />
              <CardContent className="pt-8 pb-6 px-6">
                <div className="flex items-center gap-2 mb-4">
                  <Briefcase className="w-6 h-6 text-foreground" />
                  <h3 className="text-lg font-bold text-foreground">Monthly Plans</h3>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <IndianRupee className="w-4 h-4 text-muted-foreground" />
                    <span>From ₹500/month</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span>Volume discounts</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                    <span>Priority support</span>
                  </div>
                </div>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/pricing?tab=professional">Compare Plans</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container py-20">
          <Card className="border-2 border-cyan-500/20 bg-card overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 text-sm font-medium w-fit mb-4">
                    <Sparkles className="w-3.5 h-3.5" />
                    Free for 3 months
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                    Ready to simplify your payments?
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Join professionals who stopped chasing payments and started growing their business.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild size="lg" className="px-8 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                      <Link to="/login?tab=signup">
                        Get Started
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="ghost" size="lg">
                      <Link to="/pricing">View Pricing</Link>
                    </Button>
                  </div>
                </div>
                <div className="hidden md:flex items-center justify-center bg-gradient-to-br from-cyan-500/5 to-teal-500/10 p-12">
                  <div className="grid grid-cols-2 gap-4 max-w-xs">
                    <div className="bg-background rounded-xl p-4 shadow-sm border border-border">
                      <IndianRupee className="w-8 h-8 text-cyan-600 mb-2" />
                      <div className="text-2xl font-bold text-foreground">90%</div>
                      <div className="text-xs text-muted-foreground">On-time rate</div>
                    </div>
                    <div className="bg-background rounded-xl p-4 shadow-sm border border-border">
                      <Users className="w-8 h-8 text-teal-600 mb-2" />
                      <div className="text-2xl font-bold text-foreground">∞</div>
                      <div className="text-xs text-muted-foreground">Clients</div>
                    </div>
                    <div className="bg-background rounded-xl p-4 shadow-sm border border-border col-span-2">
                      <Clock className="w-8 h-8 text-cyan-600 mb-2" />
                      <div className="text-2xl font-bold text-foreground">5 mins</div>
                      <div className="text-xs text-muted-foreground">Setup time</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>

      <footer className="border-t border-border bg-card">
        <div className="container py-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-cyan-600" />
              <span className="font-bold text-foreground">Shakatah</span>
              <span className="text-muted-foreground text-sm ml-2">Payment collections for professionals</span>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 text-sm text-muted-foreground">
              <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
              <Link to="/professional-faq" className="hover:text-foreground transition-colors">FAQ</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
      
      <FAQChatBot context="customer" />
    </div>
  );
}

export default Index;
